<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { CircleX, CircleAlert, CircleCheck, Info, X } from 'lucide-vue-next'
import type { ToastType } from '@/composables/useToast'

const props = defineProps<{
  id: number
  message: string
  type: ToastType
  duration: number
}>()

const emit = defineEmits<{
  close: [id: number]
}>()

const hiding = ref(false)
const progress = ref(100)
let timer: number | undefined
let animFrame: number | undefined
let startTime: number

const iconComponent = computed(() => {
  switch (props.type) {
    case 'error': return CircleX
    case 'warning': return CircleAlert
    case 'success': return CircleCheck
    case 'info': return Info
    default: return Info
  }
})

const toastClasses = computed(() => {
  const base = 'relative flex items-center gap-3 px-5 py-3.5 rounded-md shadow-lg border pointer-events-auto max-w-[400px] max-sm:max-w-full'
  const variants: Record<string, string> = {
    error: 'bg-red-50 border-red-500/30 dark:bg-red-500/[0.12] dark:border-red-500/30',
    warning: 'bg-amber-50 border-yellow-500/30 dark:bg-yellow-500/[0.12] dark:border-yellow-500/30',
    success: 'bg-green-50 border-green-500/30 dark:bg-green-500/[0.12] dark:border-green-500/30',
    info: 'bg-blue-50 border-blue-500/30 dark:bg-blue-500/[0.12] dark:border-blue-500/30',
  }
  return [base, variants[props.type] || variants.info, hiding.value && 'animate-toast-slide-out']
})

const iconColorClass = computed(() => {
  const map: Record<string, string> = {
    error: 'text-accent-red',
    warning: 'text-amber-600',
    success: 'text-accent-green',
    info: 'text-accent-blue',
  }
  return map[props.type] || map.info
})

const progressColorClass = computed(() => {
  const map: Record<string, string> = {
    error: 'bg-accent-red',
    warning: 'bg-warning',
    success: 'bg-accent-green',
    info: 'bg-accent-blue',
  }
  return map[props.type] || map.info
})

function startProgress() {
  startTime = Date.now()
  const tick = () => {
    const elapsed = Date.now() - startTime
    progress.value = Math.max(0, 100 - (elapsed / props.duration) * 100)
    if (progress.value > 0) {
      animFrame = requestAnimationFrame(tick)
    }
  }
  animFrame = requestAnimationFrame(tick)
}

function dismiss() {
  hiding.value = true
  setTimeout(() => emit('close', props.id), 300)
}

onMounted(() => {
  if (props.duration > 0) {
    startProgress()
    timer = window.setTimeout(dismiss, props.duration)
  }
})

onUnmounted(() => {
  if (timer) clearTimeout(timer)
  if (animFrame) cancelAnimationFrame(animFrame)
})
</script>

<template>
  <div :class="toastClasses" role="alert">
    <span :class="['flex items-center justify-center shrink-0', iconColorClass]">
      <component :is="iconComponent" :size="18" class="overflow-visible" />
    </span>
    <span class="flex-1 text-sm font-medium text-foreground">{{ message }}</span>
    <button
      class="flex items-center justify-center w-6 h-6 border-none bg-transparent text-foreground-muted rounded hover:bg-black/5 hover:text-foreground transition-all duration-200 shrink-0 cursor-pointer"
      @click="dismiss"
      aria-label="Close"
    >
      <X :size="14" />
    </button>
    <div
      v-if="duration > 0"
      :class="['absolute bottom-0 left-0 h-[3px] rounded-b-md transition-[width] linear', progressColorClass]"
      :style="{ width: progress + '%' }"
    />
  </div>
</template>
